import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { EngineNodesStateModel } from './engine.nodes.state.model';
import { HoverNode,
  SelectNode,
  CheckNode,
  CheckInverseNodes,
  SetCheckedNodes,
  ToggleVisibleNodes,
  ExpandNode,
  CollapseNode, 
  ResetEngineNodes,
  HideInverse,
  Isolate, 
  CheckAllNodes,
  TogglePickableNodes,
  ToggleInversePickableNodes, 
  QueryNodeInfo,
  QueryNodeInfoFailure,
  QueryNodeInfoSuccess,
  SetNodeInfo,
  PaintNodes,
  UnpaintNodes} from './engine.nodes.actions';
import { EngineState } from '../engine.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { EngineService } from '../../engine.service';
import { SetGraph, SynchronizePickableNodes } from '../engine.actions';
import { WikiService } from 'src/app/shared/wiki.service';
import { finalize } from 'rxjs/operators';
import { PaintService } from '../../paint.service';

const initialState = {
  hit: null,
  selectedNode: null,
  hoveredNode: null,
  checkedNodes: [],
  querying: false
};

@State<EngineNodesStateModel>({
  name: 'engineNodes',
  defaults: initialState
})
export class EngineNodesState {
  /**
   * Selectors
   */
  @Selector()
  static getHit(state: EngineNodesStateModel) {
    return state.hit;
  }

  @Selector()
  static getHoveredNode(state: EngineNodesStateModel) {
    return state.hoveredNode;
  }

  @Selector()
  static getCheckedNodeIds(state: EngineNodesStateModel) {
    return state.checkedNodes.map(node => node.id);
  }

  @Selector()
  static getCheckedNodes(state: EngineNodesStateModel) {
    return state.checkedNodes;
  }

  // TODO: is this used?
  @Selector()
  static getSelectedNode(state: EngineNodesStateModel) {
    return state.selectedNode;
  }

  @Selector()
  static getQuerying(state: EngineNodesStateModel) {
    return state.querying;
  }

  /**
   * Actions
   */
  // TODO: is this used?
  @Action(SelectNode)
  selectNode(context: StateContext<EngineNodesStateModel>, action: SelectNode) {
    context.patchState({
      selectedNode: action.node
    });
  }


  @Action(HoverNode)
  hoverNode(context: StateContext<EngineNodesStateModel>, action: HoverNode) {
    context.patchState({
      hoveredNode: action.node,
      hit: {
        ...action.hit || null
      }
    });
  }

  @Action(CheckNode)
  checkNode(context: StateContext<EngineNodesStateModel>, action: CheckNode) {
    const checkedNodes = context.getState().checkedNodes;
    
    if (action.checked) {
      // Add node
      const hasNode = checkedNodes.some((node) => node.id === action.node.id);
      if (!hasNode) {
        context.patchState({
          checkedNodes: [...checkedNodes, action.node]
        });
      }
    } else {
      // Remove node
      const nodes = checkedNodes
        .filter((node) => node.id !== action.node.id);

      context.patchState({
        checkedNodes: [...nodes]
      });
    }
  }

  @Action(CheckAllNodes)
  checkAllNodes(context: StateContext<EngineNodesStateModel>, action: CheckAllNodes) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const nodes = Object.values(viewGraph.roots);

    const leaves = this.graphService.getAllLeafDescendants(
      viewGraph,
      nodes.map(node => node.id)
    );

    const leafNodes = this.graphService.getNodes(viewGraph, leaves);
    
    // Update state with the new checked nodes
    context.patchState({
      checkedNodes: [...leafNodes]
    });
  }

  @Action(CheckInverseNodes)
  checkInverseNodes(context: StateContext<EngineNodesStateModel>, action: CheckInverseNodes) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    
    // Get actual leaves from nodes passed in
    const leaves = this.graphService.getAllLeafDescendants(
      viewGraph,
      action.nodes.map(node => node.id)
    );

    // Find the inversion of those leaves
    const inverseNodes = this.graphService.getInverseSelection(
      viewGraph,
      leaves
    );
    const nodes = this.graphService.getNodes(viewGraph, inverseNodes);

    // Update state with the new checked nodes
    context.patchState({
      checkedNodes: [...nodes]
    });
  }

  @Action(SetCheckedNodes)
  setCheckedNodes(context: StateContext<EngineNodesStateModel>, action: SetCheckedNodes) {
    context.patchState({
      checkedNodes: [...action.nodes]
    });
  }

  @Action(ToggleVisibleNodes)
  toggleVisibleNodes(context: StateContext<EngineNodesStateModel>, action: ToggleVisibleNodes) {
    const nodeIds = action.nodes.map(node => node.id);
    this.engineService.toggleNodes(nodeIds, action.visible);
  }

  @Action(TogglePickableNodes)
  togglePickableNodes(context: StateContext<EngineNodesStateModel>, action: TogglePickableNodes) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);

    const nodeIds = action.nodes.map(node => node.id);
    const changeMap = this.engineService.setNodesPickable(viewGraph, nodeIds, action.pickable);

    this.store.dispatch(new SynchronizePickableNodes(changeMap));      
  }

  @Action(ToggleInversePickableNodes)
  toggleInversePickableNodes(context: StateContext<EngineNodesStateModel>, action: ToggleInversePickableNodes) {
    const nodeIds = action.nodes.map(node => node.id);
    const inverseNodeIds = this.graphService.getInverseSelection(action.viewGraph, nodeIds);
    const nodes = this.graphService.getNodes(action.viewGraph, inverseNodeIds);

    this.store.dispatch(new TogglePickableNodes(nodes, action.pickable));
  }

  @Action(ExpandNode)
  expandNode(context: StateContext<EngineNodesStateModel>, action: ExpandNode) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const ancestors = this.graphService.getAncestors(viewGraph, action.node.id, true);
    ancestors.forEach(id => {
      viewGraph.nodes[id].boolState.expanded = true;
    });

    this.store.dispatch(new SetGraph(viewGraph));
  }

  @Action(CollapseNode)
  collapseNode(context: StateContext<EngineNodesStateModel>, action: CollapseNode) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    viewGraph.nodes[action.node.id].boolState.expanded = false;

    this.store.dispatch(new SetGraph(viewGraph));
  }

  @Action(ResetEngineNodes)
  resetEngineNodes(context: StateContext<EngineNodesStateModel>, action: ResetEngineNodes) {
    context.setState(initialState);
  }

  @Action(HideInverse)
  hideInverse(context: StateContext<EngineNodesStateModel>, action: HideInverse) {
    const nodeIds = action.nodes.map(node => node.id);
    const inverseNodeIds = this.graphService.getInverseSelection(action.viewGraph, nodeIds);
    const nodes = this.graphService.getNodes(action.viewGraph, inverseNodeIds);
    this.store.dispatch(new ToggleVisibleNodes(nodes, false));
  }

  @Action(Isolate)
  isolate(context: StateContext<EngineNodesStateModel>, action: Isolate) {
    this.store.dispatch(new HideInverse(action.nodes, action.viewGraph));
    this.store.dispatch(new ToggleVisibleNodes(action.nodes, true, false));
  }

  @Action(QueryNodeInfo)
  queryNodeInfo(context: StateContext<EngineNodesStateModel>, { node }: QueryNodeInfo) {
    // begin querying
    context.patchState({ querying: true });

    // query wiki service
    this.wikiService.getWikiText(node.label)
      .pipe(
        // end querying
        finalize(() => context.patchState({ querying: false }))
      )
      .subscribe(
        // success
        (wikiDescription) => context.dispatch(new QueryNodeInfoSuccess(node, wikiDescription)),
        // failure
        () => context.dispatch(new QueryNodeInfoFailure(node))
      );
  }

  @Action(QueryNodeInfoSuccess)
  queryNodeInfoSuccess(context: StateContext<EngineNodesStateModel>, { node, description }: QueryNodeInfoSuccess) {
    context.dispatch(new SetNodeInfo(node, description));
  }

  @Action(SetNodeInfo)
  setNodeInfo(context: StateContext<EngineNodesStateModel>, { node, description }: SetNodeInfo) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    const viewNode = viewGraph.nodes[node.id];
    if (viewNode) {
      viewNode.description = description;
      this.store.dispatch(new SetGraph(viewGraph));
      const selectedNode = context.getState().selectedNode;
      /*
      context.patchState({
        selectedNode: null
      });
      */
      context.patchState({
        selectedNode: selectedNode
      });     
    }
  }
  
  @Action(PaintNodes)
  paintNodes(context: StateContext<EngineNodesStateModel>, { nodes, recipe }: PaintNodes) {
    this.paintService.paintAll(nodes, recipe);
  }

  @Action(UnpaintNodes)
  unpaintNodes(context: StateContext<EngineNodesStateModel>, { nodes }: UnpaintNodes) {
    nodes.forEach((node) => {
      this.paintService.unpaint(node);
    });
  }  

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private engineService: EngineService,
    private wikiService: WikiService,
    private paintService: PaintService
  ) { }
}
